import { useLayoutEffect, useMemo, useRef } from 'react';
import { useForceRender } from './useForceRender';

/**
 * Create new observable ref (without re-render)
 * @param initialValue
 * @return {{readonly current: *, subscribe(*=): void, unsubscribe(*=): void, notify: (function(): void)}}
 * todo: useRef() seems to be redundant here. simply use `let current=initialValue` inside useMamo()
 * todo: consider useCallbackRef instead of the manual setter approach
 * todo: opensource
 */
export const useObservableRef = (initialValue) => {
	const initialValueRef = useRef(initialValue);
	return useMemo(() => {
		let { current } = initialValueRef;
		const observers = new Set();
		const notify = () => {
			observers.forEach((fn) => {
				fn(current);
			});
		};
		return {
			set current(value) {
				if (current !== value) {
					current = value;
					notify();
				}
			},
			get current() {
				return current;
			},
			subscribe(handler) {
				observers.add(handler);
			},
			unsubscribe(handler) {
				observers.delete(handler);
			},
			notify,
		};
	}, []);
};

/**
 * Subscribe to ref changes (without re-render)
 * @param observableRef
 * @param {function} handler - called when ref's value is changed. Doesn't cause re-renders (safe to pass function directly)
 */
export const useObserveRef = (observableRef, handler, debugLabel) => {
	// Always call latest handler. This prevents from unnecessary re-renders when handler is changed
	const handlerRef = useRef();
	handlerRef.current = handler;
	if (!observableRef) {
		throw new Error('observableRef not defined');
	}
	useLayoutEffect(() => {
		const onNotify = (data) => {
			debugLabel && console.log(debugLabel, data);
			return handlerRef.current?.(data);
		};
		observableRef.subscribe(onNotify);
		return () => observableRef.unsubscribe(onNotify);
	}, [debugLabel, observableRef]);
};

/**
 * Re-render & get latest ref value
 * @param observableRef
 * @return {*}
 */
export const useObservableRefValue = (observableRef) => {
	const [, forceRender] = useForceRender();
	// timeout is needed in order to avoid rendering while another component is being rendered
	useObserveRef(observableRef, () => setTimeout(forceRender, 0));
	return observableRef.current;
};
